import { ComponentProps } from 'react';
import { useReduxState } from 'hooks';
import { ProgressCircle, Tooltip } from 'components/common';
import { Icon } from 'components/common/icons';
import type { IconProp } from 'components/common/icons/Icon';

export type BadgeCategory =
  | 'dark'
  | 'default'
  | 'error'
  | 'error-light'
  | 'main'
  | 'success'
  | 'success-light'
  | 'transparent'
  | 'warning'
  | 'warning-light';

export type BadgeSize = 'small' | 'medium' | 'large';

interface Props extends React.HTMLAttributes<HTMLElement> {
  category?: BadgeCategory;
  size?: BadgeSize;
  icon?: IconProp;
  iconCircleProgress?: number;
  iconStyle?: React.CSSProperties;
  isGrouped?: boolean;
  isGroupedFirst?: boolean;
  isGroupedLast?: boolean;
  isRounded?: boolean;
  newItems?: React.ReactNode;
  onClose?: () => void;
  text?: string | React.ReactNode;
  tooltip?: string | React.ReactNode;
  tooltipProps?: Partial<ComponentProps<typeof Tooltip>>;
}

const Badge = ({
  category = 'default',
  icon,
  iconCircleProgress,
  iconStyle,
  isGrouped,
  isGroupedFirst,
  isGroupedLast,
  isRounded = true,
  newItems,
  onClose,
  size = 'medium',
  text,
  tooltip,
  tooltipProps = {},
  ...props
}: Props) => {
  const { messages, theme } = useReduxState();

  return (
    <Tooltip childrenWrapInDiv={false} content={tooltip} {...tooltipProps}>
      <div
        className={`badge badge-${size} badge-${category} ${
          isRounded ? 'badge-rounded' : ''
        } ${onClose ? 'badge-with-close' : ''} ${
          isGrouped ? 'badge-grouped' : ''
        } ${isGroupedFirst ? 'badge-grouped-first' : ''} ${
          isGroupedLast ? 'badge-grouped-lasted' : ''
        }`}
        {...props}
      >
        {iconCircleProgress !== undefined ? (
          <div className="progress-circle">
            <ProgressCircle
              backgroundColor="inherit"
              borderWidth={2}
              borderColor="rgba(0, 0, 0, 0.1)"
              borderProgressColor={
                {
                  dark: theme.textColorLight,
                  default: theme.textColorLight,
                  error: theme.dangerTextColor,
                  'error-light': theme.dangerLightTextColor,
                  main: theme.mainTextColor,
                  success: theme.successTextColor,
                  'success-light': theme.successLightTextColor,
                  transparent: theme.textColorLight,
                  warning: theme.warningTextColor,
                  'warning-light': theme.warningLightTextColor
                }[category] || theme.textColorLight
              }
              progress={iconCircleProgress}
              style={{ margin: '-2px 0 -2px -1px' }}
              width={
                {
                  small: 12,
                  medium: 14,
                  large: 16
                }[size] || 14
              }
            />
          </div>
        ) : icon ? (
          <div className={`icon icon-${size}`}>
            <Icon icon={icon} style={iconStyle} />
          </div>
        ) : null}

        {text}

        {tooltip ? <span className="sr-only">{tooltip}</span> : null}

        {newItems ? <div className="new-items">{newItems}</div> : null}

        {onClose ? (
          <button
            aria-label={messages.commonRemove}
            className="close"
            onClick={onClose}
          >
            <Icon icon="times" />
          </button>
        ) : null}

        <style jsx>{`
          .badge {
            align-items: center;
            border-radius: 6px;
            display: inline-flex;
            font-weight: 600;
            line-height: normal;
            position: relative;
            white-space: nowrap;
          }
          .badge-rounded,
          .badge-small.badge-rounded {
            border-radius: 50px;
          }
          .badge-small {
            border-radius: 5px;
            font-size: 1.1rem;
            gap: 5px;
            padding: 3px 8px 3px;
          }
          .badge-medium {
            font-size: 1.3rem;
            gap: 6px;
            padding: 4px 9px 4px;
          }
          .badge-large {
            font-size: 1.4rem;
            gap: 6px;
            padding: 5px 11px 5px;
          }
          .badge-dark {
            background-color: ${theme.lightGreyDark};
            color: ${theme.textColorLight};
          }
          .badge-default {
            background-color: ${theme.lightGrey};
            color: ${theme.textColorLight};
          }
          .badge-error {
            background-color: ${theme.dangerColor};
            color: ${theme.dangerTextColor};
          }
          .badge-error-light {
            background-color: ${theme.dangerLightColor};
            color: ${theme.dangerLightTextColor};
          }
          .badge-main {
            background-color: ${theme.mainColor};
            color: ${theme.mainTextColor};
          }
          .badge-success {
            background-color: ${theme.successColor};
            color: ${theme.successTextColor};
          }
          .badge-success-light {
            background-color: ${theme.successLightColor};
            color: ${theme.successLightTextColor};
          }
          .badge-warning {
            background-color: ${theme.warningColor};
            color: ${theme.warningTextColor};
          }
          .badge-warning-light {
            background-color: ${theme.warningLightColor};
            color: ${theme.warningLightTextColor};
          }
          .icon {
            align-items: center;
            display: flex;
          }
          .icon-small {
            font-size: 0.9rem;
            min-height: 14px;
          }
          .icon-medium {
            font-size: 1.1rem;
            min-height: 17px;
          }
          .icon-large {
            font-size: 1.2rem;
            min-height: 18px;
          }
          .new-items {
            background-color: ${theme.dangerColor};
            border-radius: 10px;
            color: white;
            font-size: 1rem;
            font-weight: 700;
            line-height: 1;
            padding: 2px 5px;
            position: absolute;
            right: -4px;
            top: -6px;
          }
          .close {
            background: transparent;
            border: 0;
            border-radius: 50px;
            color: inherit;
            font-size: 1.3rem;
            line-height: 1;
            position: absolute;
            transition: background-color 0.3s;
            text-align: center;
          }
          .close:focus,
          .close:hover {
            background-color: rgba(0, 0, 0, 0.1);
            cursor: pointer;
          }
          .badge-small.badge-with-close {
            padding-right: 26px;
          }
          .badge-small .close {
            font-size: 1.1rem;
            padding: 2px 0;
            right: 4px;
            top: 4px;
            width: 15px;
            height: 15px;
          }
          .badge-medium.badge-with-close {
            padding-right: 30px;
          }
          .badge-medium .close {
            font-size: 1.2rem;
            padding: 4px 0;
            right: 4px;
            top: 2px;
            width: 20px;
            height: 20px;
          }
          .badge-large.badge-with-close {
            padding-right: 34px;
          }
          .badge-large .close {
            font-size: 1.3rem;
            padding: 3px 0;
            right: 4px;
            top: 3px;
            width: 22px;
            height: 22px;
          }
          .badge-grouped {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }
          .badge-grouped-first {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }
          .badge-grouped-lasted {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }
        `}</style>
      </div>
    </Tooltip>
  );
};

export default Badge;
